import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createBusinessStaff,
  deleteBusinessScheduleException,
  listBusinessScheduleExceptions,
  listBusinessStaff,
  listBusinessWorkSchedules,
  updateBusinessStaff,
  upsertBusinessScheduleException,
} from './staff-api';
import type {
  BusinessScheduleException,
  BusinessStaff,
  BusinessWorkSchedule,
  UpsertBusinessScheduleExceptionPayload,
  UpsertBusinessStaffPayload,
} from './types';

export const businessStaffKeys = {
  all: ['business-staff'] as const,
  list: () => [...businessStaffKeys.all, 'list'] as const,
  schedules: (staffId: string) => [...businessStaffKeys.all, 'schedules', staffId] as const,
  exceptions: (staffId: string) => [...businessStaffKeys.all, 'exceptions', staffId] as const,
};

export function useBusinessStaff() {
  return useQuery({
    queryKey: businessStaffKeys.list(),
    queryFn: listBusinessStaff,
    staleTime: 30_000,
  });
}

export function useCreateBusinessStaff() {
  const qc = useQueryClient();
  return useMutation<BusinessStaff, Error, UpsertBusinessStaffPayload>({
    mutationFn: createBusinessStaff,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: businessStaffKeys.all });
    },
  });
}

export function useUpdateBusinessStaff() {
  const qc = useQueryClient();
  return useMutation<
    BusinessStaff,
    Error,
    { staffId: string; payload: Partial<UpsertBusinessStaffPayload> }
  >({
    mutationFn: ({ staffId, payload }) => updateBusinessStaff(staffId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: businessStaffKeys.all });
    },
  });
}

export function useBusinessWorkSchedules(staffId: string | undefined) {
  return useQuery<BusinessWorkSchedule[]>({
    queryKey: businessStaffKeys.schedules(staffId ?? '__none__'),
    queryFn: () => {
      if (!staffId) return Promise.resolve([]);
      return listBusinessWorkSchedules(staffId);
    },
    enabled: Boolean(staffId),
  });
}

export function useBusinessScheduleExceptions(staffId: string | undefined) {
  return useQuery<BusinessScheduleException[]>({
    queryKey: businessStaffKeys.exceptions(staffId ?? '__none__'),
    queryFn: () => {
      if (!staffId) return Promise.resolve([]);
      return listBusinessScheduleExceptions(staffId);
    },
    enabled: Boolean(staffId),
  });
}

export function useUpsertBusinessScheduleException(staffId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<BusinessScheduleException, Error, UpsertBusinessScheduleExceptionPayload>({
    mutationFn: (payload) => {
      if (!staffId) return Promise.reject(new Error('staffId is required'));
      return upsertBusinessScheduleException(staffId, payload);
    },
    onSuccess: () => {
      if (staffId) qc.invalidateQueries({ queryKey: businessStaffKeys.exceptions(staffId) });
    },
  });
}

export function useDeleteBusinessScheduleException(staffId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (exceptionId) => {
      if (!staffId) return Promise.reject(new Error('staffId is required'));
      return deleteBusinessScheduleException(staffId, exceptionId);
    },
    onSuccess: () => {
      if (staffId) qc.invalidateQueries({ queryKey: businessStaffKeys.exceptions(staffId) });
    },
  });
}
